// Club-Network API client (N2 club-facing + N3 Trusted Agent).
//
// Isolated from the shared services/api.js on purpose (small PR surface, no risk
// to the shared axios instance) — mirrors services/agentTenderApi.js.
//
// Paths + shapes are the FINAL cn-env contract (Backend #23): N2 lives under
// `/dashboard/club/agent-requests/`, N3 under `/dashboard/agent-search-requests/`.
// Mock switch: REACT_APP_CN_MOCK === "1" serves everything from
// services/clubNetworkMock.js (same shapes) so the UI runs without a backend.
import axios from "axios";
import clubNetworkMock from "./clubNetworkMock";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;
const USE_MOCK = process.env.REACT_APP_CN_MOCK === "1";

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Same Bearer-token attachment the main client uses (authSlice persists
// localStorage "tokens" = { access, refresh }).
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token available — request proceeds and the server returns 401 */
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    "Something went wrong. Please try again.",
});

// GET /dashboard/club/my-squad/ returns { results: { club, competition, season,
// squad: [...] }, success, message } (MyClubSquadView) — the array lives at
// data.results.squad, NOT data.results. Fallbacks keep older/simpler shapes
// ({ squad: [...] }, { results: [...] }, bare array) working.
export const extractSquadRows = (data) =>
  data?.results?.squad ||
  data?.squad ||
  (Array.isArray(data?.results) ? data.results : null) ||
  (Array.isArray(data) ? data : []);

const listPagination = (data, params) => ({
  count: data?.count ?? (Array.isArray(data?.results) ? data.results.length : 0),
  next: data?.next ?? null,
  previous: data?.previous ?? null,
  currentPage: parseInt(
    (params instanceof URLSearchParams ? params.get("page") : params?.page) || "1",
    10,
  ),
});

// ── N2 — club-facing Agent Requests (base /dashboard/club/agent-requests/) ───
// Feed reuses the IDENTICAL AgentTenderFeedSerializer (no club field). `you_contacted`
// is NOT on feed items (D-003) — it comes from the detail endpoint or the batch
// `contacted/` endpoint; `chat_channel_id` only from the contact response.
export const clubNetworkService = {
  getRequests: async (params, signal) => {
    if (USE_MOCK) return clubNetworkMock.getRequests(params);
    try {
      const res = await authApi.get("/dashboard/club/agent-requests/", {
        params,
        signal,
      });
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, params),
        // Category-pill totals (C13/P1): {players, coaches, staffs, others},
        // computed without the active category pill so each badge shows its total.
        tabCounts: res.data?.tab_counts || null,
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  getRequest: async (id) => {
    if (USE_MOCK) return clubNetworkMock.getRequest(id);
    try {
      const res = await authApi.get(`/dashboard/club/agent-requests/${id}/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Batch "have I already contacted this agent's request" for the current feed
  // page — agent-granular, avoids N+1. Returns a Set of contacted request ids.
  getContacted: async (ids, signal) => {
    if (USE_MOCK) return clubNetworkMock.getContacted(ids);
    try {
      const res = await authApi.get("/dashboard/club/agent-requests/contacted/", {
        params: { ids: (ids || []).join(",") },
        signal,
      });
      // `contacted` = ids reached out to (club-wide); `attribution` = per-id
      // { contacted_by:{name,avatar}, contacted_at } — present only for club-wide
      // records, so an old contact appears in `contacted` but not `attribution`
      // (DoD 27 graceful "Contacted" without a name).
      return {
        success: true,
        data: res.data?.contacted || [],
        attribution: res.data?.attribution || {},
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Batch "which of these agent-requests has this club saved" for the current
  // feed page (C12). Mirrors getContacted — the feed list stays byte-identical to
  // the anonymised agency feed (is_saved is NOT inline), so saved state comes from
  // this side channel. Returns an array of saved request ids.
  getSaved: async (ids, signal) => {
    if (USE_MOCK) return clubNetworkMock.getSaved(ids);
    try {
      const res = await authApi.get("/dashboard/club/agent-requests/saved/", {
        params: { ids: (ids || []).join(",") },
        signal,
      });
      return { success: true, data: res.data?.saved || [] };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Save / unsave an agent-request (C12). Reuses the generic, role-open
  // SavedTender endpoints — NOT a club-network-specific bookmark path. The
  // endpoints are NOT idempotent (double-save → 400, unsave-when-absent → 404),
  // so callers toggle on the known is_saved/saved state, never blind-post.
  saveRequest: async (id) => {
    if (USE_MOCK) return clubNetworkMock.saveRequest(id);
    try {
      await authApi.post(`/dashboard/tenders/${id}/save_tender/`);
      return { success: true };
    } catch (error) {
      return fail(error);
    }
  },

  unsaveRequest: async (id) => {
    if (USE_MOCK) return clubNetworkMock.unsaveRequest(id);
    try {
      await authApi.post(`/dashboard/tenders/${id}/unsave_tender/`);
      return { success: true };
    } catch (error) {
      return fail(error);
    }
  },

  // The club's own first-team squad, for the optional player-attach picker.
  // Mapped to the compact {name, position, age} the contact attach needs.
  getSquad: async (signal) => {
    if (USE_MOCK) return clubNetworkMock.getSquad();
    try {
      const res = await authApi.get("/dashboard/club/my-squad/", { signal });
      const rows = extractSquadRows(res.data);
      const squad = (Array.isArray(rows) ? rows : []).map((p) => ({
        id: p.id,
        name: p.name,
        position: p.position || p.tp_detailed_position || "",
        age: p.age ?? null,
      }));
      return { success: true, data: squad };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Start/ensure the direct chat with the request's responsible agent and send
  // the first message. `player` (optional) becomes a player_share chat attachment
  // (D-004). Returns { channel_id, channel_type, you_contacted }.
  messageAgent: async (id, { text, player } = {}) => {
    if (USE_MOCK) return clubNetworkMock.messageAgent(id, { text, player });
    try {
      const body = {};
      if (text) body.message = text;
      if (player) {
        body.player = { name: player.name, position: player.position, age: player.age };
      }
      const res = await authApi.post(
        `/dashboard/club/agent-requests/${id}/contact/`,
        body,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

// ── N3 — Trusted Agent (base /dashboard/agent-search-requests/) ──────────────
// Standalone from tenders. Field names are the backend's canonical spec set
// (position_primary, window, level_expectation, additional_details, single
// position_secondary, flat terms_*). {id} on accept/decline/close is the
// SEARCH-REQUEST id, never a recipient id.
export const trustedAgentService = {
  // `opts` = { status?, bucket? }. `bucket` is the A15 Active/Archive tab
  // (?bucket=active|archive, derived server-side — NOT ?status=closed). The
  // list response carries active_count/archive_count for the tab pills.
  listRequests: async (opts, signal) => {
    // Back-compat: a bare string is still treated as the legacy `status`.
    const { status, bucket } =
      typeof opts === "string" ? { status: opts } : opts || {};
    if (USE_MOCK) return clubNetworkMock.listSearchRequests({ status, bucket });
    try {
      const params = {};
      if (status) params.status = status;
      if (bucket) params.bucket = bucket;
      const res = await authApi.get("/dashboard/agent-search-requests/", {
        params,
        signal,
      });
      return {
        success: true,
        data: res.data.results || res.data || [],
        activeCount: res.data?.active_count ?? null,
        archiveCount: res.data?.archive_count ?? null,
        pagination: listPagination(res.data, {}),
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // A14b — withdraw the still-pending invitations. Server refuses with 409 once
  // an agent accepted (use close instead).
  withdrawRequest: async (id) => {
    if (USE_MOCK) return clubNetworkMock.withdrawSearchRequest(id);
    try {
      const res = await authApi.post(`/dashboard/agent-search-requests/${id}/withdraw/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // A14 Seen trigger — the agent's FE calls this when it opens the request view
  // in full (B8): the recipient advances sent → seen (idempotent server-side, a
  // no-op once seen or answered), so the club's Pending chip flips to Seen.
  markSeen: async (id) => {
    if (USE_MOCK) return clubNetworkMock.markSeen(id);
    try {
      const res = await authApi.post(`/dashboard/agent-search-requests/${id}/seen/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // A7 — the commissioning club's READ-ONLY view of the agent's LIVE
  // commissioned request: the anonymised feed card content + the two chips that
  // exist only here (Club Verified + "Commissioned by you"). No player replies.
  getCommissionedRequest: async (id, signal) => {
    if (USE_MOCK) return clubNetworkMock.getCommissionedRequest(id);
    try {
      const res = await authApi.get(
        `/dashboard/agent-search-requests/${id}/commissioned-request/`,
        { signal },
      );
      return { success: true, data: res.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  getRequest: async (id, signal) => {
    if (USE_MOCK) return clubNetworkMock.getSearchRequest(id);
    try {
      const res = await authApi.get(`/dashboard/agent-search-requests/${id}/`, {
        signal,
      });
      return { success: true, data: res.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Create a search request. When an optional `document` File is attached the
  // request is sent as multipart (DoD 22); otherwise a plain JSON body. Null/
  // undefined fields are dropped so only filled spec fields travel.
  createRequest: async (payload) => {
    if (USE_MOCK) return clubNetworkMock.createSearchRequest(payload);
    try {
      const hasFile = typeof File !== "undefined" && payload.document instanceof File;
      let body;
      if (hasFile) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          if (k === "recipient_agent_ids") {
            v.forEach((id) => fd.append("recipient_agent_ids", id));
          } else if (k === "document") {
            fd.append("document", v);
          } else if (typeof v === "boolean") {
            fd.append(k, v ? "true" : "false");
          } else {
            fd.append(k, v);
          }
        });
        body = fd;
      } else {
        const { document, ...rest } = payload;
        body = rest;
      }
      const res = await authApi.post("/dashboard/agent-search-requests/", body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Mint a short-lived presigned URL for the attached document (DoD 22). Gated
  // server-side to the owning club or a recipient agent; returns { download_url }.
  getDocumentUrl: async (id) => {
    if (USE_MOCK) return clubNetworkMock.getSearchRequestDocument(id);
    try {
      const res = await authApi.get(`/dashboard/agent-search-requests/${id}/document/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  closeRequest: async (id) => {
    if (USE_MOCK) return clubNetworkMock.closeSearchRequest(id);
    try {
      const res = await authApi.post(`/dashboard/agent-search-requests/${id}/close/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Recipient picker — reuses the platform agent search (SearchViewSet).
  // license_verification_status + is_licensed feed the FIFA badge gate
  // (isFifaVerified — Marin 2026-08-04: self-declaration carries the badge,
  // only 'failed_qr' hides it; never an "Unlicensed" label).
  searchAgents: async (q = "", signal) => {
    if (USE_MOCK) return clubNetworkMock.searchAgents(q);
    try {
      const res = await authApi.get("/dashboard/search/", {
        params: { type: "agents", query: q },
        signal,
      });
      const rows = res.data?.results?.agents || res.data?.results || res.data || [];
      // Normalise the platform agent-search shape (name / agency{name} /
      // profile_picture_url / license_number) into the picker's contract
      // ({ id, name, avatar, agency_name, is_licensed }). `id` is the Agent PK —
      // exactly what recipient_agent_ids expects.
      const data = (Array.isArray(rows) ? rows : []).map((a) => ({
        id: a.id,
        name: a.name,
        avatar: a.avatar || a.profile_picture_url || null,
        agency_name: a.agency_name || a.agency?.name || "",
        agency_country: a.agency_country || a.agency?.country || "",
        is_licensed:
          typeof a.is_licensed === "boolean"
            ? a.is_licensed
            : !!(a.license_number && String(a.license_number).trim()),
        license_verification_status:
          a.license_verification_status || "unverified",
      }));
      return { success: true, data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Agent side: incoming search requests (club FULLY visible, unlike N2).
  listInbox: async (status, signal) => {
    if (USE_MOCK) return clubNetworkMock.listInbox(status);
    try {
      const res = await authApi.get("/dashboard/agent-search-requests/inbox/", {
        params: status ? { status } : {},
        signal,
      });
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, {}),
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Agent side — accept opens a chat with the club; decline takes an optional
  // reason. `id` is the SEARCH-REQUEST id (the agent's recipient row is resolved
  // server-side from the authed user).
  acceptInbox: async (id) => {
    if (USE_MOCK) return clubNetworkMock.acceptInbox(id);
    try {
      const res = await authApi.post(`/dashboard/agent-search-requests/${id}/accept/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  declineInbox: async (id, reason) => {
    if (USE_MOCK) return clubNetworkMock.declineInbox(id, reason);
    try {
      const res = await authApi.post(
        `/dashboard/agent-search-requests/${id}/decline/`,
        { reason: reason || "" },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // B2 — the hand-over prefill for the EXISTING Agent Request form. Returns
  // { search_request_id, commissioning_club_id, verified_from_birth,
  //   locked_fields:[form-field names], prefill:{form-field: value},
  //   terms_not_carried:[...], no_tender_pendant:[...] }. The core criteria are
  // server-enforced (LOCKED, Marin) — the FE renders them read-only; the
  // commission (salary/budget/commission) is NEVER in `prefill` by design.
  getCommissionPrefill: async (id, signal) => {
    if (USE_MOCK) return clubNetworkMock.getCommissionPrefill(id);
    try {
      const res = await authApi.get(
        `/dashboard/agent-search-requests/${id}/commission-prefill/`,
        { signal },
      );
      return { success: true, data: res.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // ── QCS-T5 Delivery (Annex A2/A6·b/B3) ────────────────────────────────────
  // Agent side — the ACCEPTED players on the commissioned request, the deliver
  // checklist's data source (B3). Player identity is hydrated from the record
  // (photo/name/pos/age/MV/contract + the player's own agent), never free-typed
  // (Annex Z.7). NOTE: this reads the accepted-players GET the deliver flow
  // needs; the checklist rows carry the SAME hydrated player shape the proposal
  // cards use, so nothing is retyped.
  getAcceptedPlayers: async (id, signal) => {
    if (USE_MOCK) return clubNetworkMock.getAcceptedPlayers(id);
    try {
      const res = await authApi.get(
        `/dashboard/agent-search-requests/${id}/accepted-players/`,
        { signal },
      );
      return { success: true, data: res.data?.results || res.data || [] };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Agent side — deliver the curated proposal package (B3b) or the honest empty
  // end (A3). `proposals` is the ticked list of { player_id, note? }; an empty
  // list REQUIRES `emptyReason`. The server re-validates every player_id against
  // the accepted set, so a client can never smuggle a foreign player (Annex B3).
  deliver: async (id, { proposals = [], summaryNote = "", emptyReason = "" } = {}) => {
    if (USE_MOCK) return clubNetworkMock.deliver(id, { proposals, summaryNote, emptyReason });
    try {
      const body = { proposals };
      if (summaryNote) body.summary_note = summaryNote;
      if (emptyReason) body.empty_reason = emptyReason;
      const res = await authApi.post(
        `/dashboard/agent-search-requests/${id}/deliver/`,
        body,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Club side — the read-only delivery view (A2/A3). Header (proposal_count,
  // delivered_at, agent, searching_since), the agent's summary_note, and the
  // A6·b proposal cards (player data hydrated from the record). Empty delivery
  // carries `empty_reason` and no cards.
  getDelivery: async (id, signal) => {
    if (USE_MOCK) return clubNetworkMock.getDelivery(id);
    try {
      const res = await authApi.get(
        `/dashboard/agent-search-requests/${id}/delivery/`,
        { signal },
      );
      return { success: true, data: res.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Club side — open the club↔player's-agent thread from a proposal card (A8).
  // "The delivery is the invitation": no accept-gate on the agent side. Returns
  // { search_request_id, player_id, channel, context_card }.
  contactAgent: async (id, playerId) => {
    if (USE_MOCK) return clubNetworkMock.contactAgent(id, playerId);
    try {
      const res = await authApi.post(
        `/dashboard/agent-search-requests/${id}/contact-agent/`,
        { player_id: playerId },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export const CLUB_NETWORK_MOCK_ENABLED = USE_MOCK;
